import React from "react";
import {
  FiMessageCircle,
  FiHelpCircle,
  FiClipboard,
  FiShield,
  FiSmartphone,
  FiClock,
} from "react-icons/fi";
import { MdOutlineSupportAgent, MdAutoFixHigh } from "react-icons/md";
import AIAutomationTemplate from "./AIAutomationTemplate";

const AISupportBot = () => {
  const features = [
    {
      title: "Customer Support Automation",
      description:
        "AI bots triage, resolve, and escalate tickets across chat, email, and social channels with consistent SLAs.",
      icon: <MdOutlineSupportAgent />,
    },
    {
      title: "Ticket Resolution Engine",
      description:
        "Context-aware workflows pull knowledge articles, policies, and previous resolutions to close tickets autonomously.",
      icon: <FiClipboard />,
    },
    {
      title: "FAQ Automation",
      description:
        "Continuously updated FAQ brains resolve repetitive queries instantly, freeing human agents for complex cases.",
      icon: <FiHelpCircle />,
    },
    {
      title: "Context-Aware Responses",
      description:
        "Bots leverage CRM, order, and device data to personalize answers and anticipate next-best actions.",
      icon: <FiMessageCircle />,
    },
    {
      title: "Multichannel Support",
      description:
        "Deploy across web chat, WhatsApp, SMS, voice, and in-app channels with unified analytics.",
      icon: <FiSmartphone />,
    },
    {
      title: "Quality & Compliance Guardrails",
      description:
        "Real-time monitoring enforces tone, regulatory disclaimers, and secure data sharing.",
      icon: <FiShield />,
    },
  ];

  const steps = [
    {
      title: "Support Journey Mapping",
      description:
        "Document customer intents, escalation paths, and SLAs to prioritize the highest-impact automations.",
    },
    {
      title: "Knowledge Ingestion",
      description:
        "Index knowledge bases, past tickets, and product docs with retrieval-augmented generation pipelines.",
    },
    {
      title: "Bot Build & Integration",
      description:
        "Configure bots, connect to CRMs, ITSMs, and commerce systems, and define guardrails for safe automation.",
    },
    {
      title: "Continuous Improvement",
      description:
        "Monitor CSAT, deflection, and containment to iteratively expand coverage and accuracy.",
    },
  ];

  const benefits = [
    "24/7 support coverage without additional headcount",
    "Lower average handle time and faster first response",
    "Higher first-contact resolution with contextual knowledge",
    "Consistent tone, policy adherence, and compliance",
    "Actionable insights into trending issues and gaps",
    "Improved agent productivity via AI-generated summaries",
  ];

  const expertise = [
    "ITIL and CX strategists specializing in omnichannel support",
    "Integrations across Zendesk, Freshdesk, ServiceNow, and Jira Service Management",
    "Fine-tuned LLMs with enterprise retrieval and sensitive data masking",
    "Human-in-the-loop workflow design for complex escalations",
    "Voice + chat parity ensuring consistent experiences everywhere",
    "Automation playbooks for onboarding, billing, logistics, and tech support",
  ];

   const image = [
    {
      side_image: "../assets/images/ai-automation/3.avif ",
    }

  ];

  const useCases = [
    {
      industry: "E-commerce",
      title: "Order & Returns Desk",
      description:
        "Bots provide shipment updates, initiate returns, and issue store credits across channels.",
    },
    {
      industry: "Telecom",
      title: "Network Diagnostics",
      description:
        "Support bot runs automated line tests, books technicians, and updates OSS/BSS records.",
    },
    {
      industry: "SaaS",
      title: "Tier-1 Deflection",
      description:
        "Context-aware assistant resolves 65% of tickets by referencing product telemetry and knowledge articles.",
    },
    {
      industry: "Healthcare",
      title: "Patient Helpdesk",
      description:
        "Secure bot manages portal access, claim status, and appointment FAQs while logging HIPAA-compliant notes.",
    },
  ];

  const reasons = [
    "CX-focused discovery aligning KPIs with automation targets",
    "Pre-built connectors for major CRM, ITSM, and commerce stacks",
    "Dedicated AI quality lab monitoring hallucinations and drift",
    "Playbooks for multilingual and accessibility-compliant experiences",
    "Model-agnostic approach leveraging open and proprietary LLMs",
    "Governance framework with audit logging and change control",
  ];

  return (
    <AIAutomationTemplate
      title="AI Support Bot"
      heroDescription="Deliver always-on customer care with bots that understand context, solve problems, and escalate to humans seamlessly."
      about={{
        heading: "About AI Support Bot Services",
        description:
          "We build enterprise-grade support automation that blends conversational AI, knowledge orchestration, and robust guardrails to meet modern CX expectations.",
      }}
      features={features}
      steps={steps}
      benefits={benefits}
      expertise={expertise}
      image={image}
      useCases={useCases}
      reasons={reasons}
      ctaLabel="Modernize Support Operations"
      meta={{
        title: "AI Support Bot | Customer Service Automation | Ayanshtech",
        description:
          "Customer support automation covering ticket resolution, FAQ automation, multichannel experiences, and context-aware responses.",
        ogTitle: "AI Support Bot Solutions",
        ogDescription:
          "Automate customer care with bots built for ticket resolution, FAQs, and multichannel engagement.",
        ogUrl: "http://ayanshtechnology.com/ai-support-bot",
        canonical: "http://ayanshtechnology.com/ai-support-bot",
      }}
    />
  );
};

export default AISupportBot;


